import React, { useContext } from "react"
import { DeepReadonly } from "ts-essentials"
import { FactorsViewContext } from "connected-components/dashboard/context/factors-view"
import AnalyticsContext, { useAnalyticsWithProperties } from "connected-components/analytics/context"
import { useExtractFactorsViewContextProperties } from "./extract-context-properties"

interface FactorsViewAnalyticsProviderProps {
  children: React.ReactNode
}

/**
 * Provides factors view analytics context with automatic property extraction
 * and "Factors View" prefix for all events.
 *
 * This provider wraps children with analytics context that includes:
 * - Factors view-specific context properties (viewId, sortMetricId, etc.)
 * - "Factors View" prefix for all tracked events
 * - Inheritance from parent analytics contexts if nested (Dashboard properties)
 *
 * Must be used within a FactorsViewContext.Provider.
 */
export function FactorsViewAnalyticsProvider({
  children,
}: DeepReadonly<FactorsViewAnalyticsProviderProps>) {
  const factorsViewContext = useContext(FactorsViewContext)

  const contextProperties = useExtractFactorsViewContextProperties(factorsViewContext)

  const analyticsContextValue = useAnalyticsWithProperties(contextProperties, "Factors View")

  return (
    <AnalyticsContext.Provider value={analyticsContextValue}>
      {children}
    </AnalyticsContext.Provider>
  )
}

export default FactorsViewAnalyticsProvider
